"use client";

import { GSI_CALLBACK_PATH, GSI_CREDENTIAL_COOKIE, GSI_RETURN_COOKIE, gsiShouldRedirect } from "./gsiRedirect";
import { ArrowRight, Check, Eye, EyeOff, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

/*
 * Presentation of the Sanity Android app's sign-up screens (AuthScreen.kt, OnboardingScreen.kt,
 * Theme.kt), ported 1:1 (dp = px, sp = px). Theme: violet scheme, light mode.
 */


export function SanityLogo({ height, loader = false }: { height: number; loader?: boolean }) {
  return (
    <svg className={loader ? "sw-logo sw-logo-loader" : "sw-logo"} viewBox="0 0 500 278" height={height} width={(height * 500) / 278} aria-hidden="true">
      <path className="sw-logo-cloud" d="M80.375 154.306C75.034 141.243 72.09 126.945 72.09 111.96C72.09 50.126 122.216 0 184.05 0C224.318 0 259.621 21.259 279.348 53.165C294.949 40.999 314.573 33.75 335.89 33.75C386.711 33.75 427.91 74.949 427.91 125.77C427.91 135.348 426.447 144.585 423.731 153.268C428.046 152.333 432.525 151.84 437.12 151.84C471.848 151.84 500 179.992 500 214.72C500 249.448 471.848 277.6 437.12 277.6H437.11V277.61H62.88V277.6C28.152 277.6 0 249.448 0 214.72C0 179.992 28.152 151.84 62.88 151.84C68.951 151.84 74.821 152.7 80.375 154.306Z" />
      <path fill="#fff" d="M202.402 92.114C203.868 91.998 214.825 91.904 229.11 91.841V91.732H276.63V92.241Q280.793 91.786 285.351 91.786Q301.416 91.786 312.567 97.551Q323.718 103.315 330.144 113.332Q336.57 123.349 338.082 136.39L313.323 137.524Q312.378 130.153 308.882 124.672Q305.385 119.191 299.432 116.073Q293.478 112.954 284.973 112.954Q274.389 112.954 268.247 117.774Q262.104 122.593 262.104 130.531Q262.104 136.012 264.656 139.603Q267.207 143.194 273.822 145.745Q280.437 148.297 292.344 151.132Q310.299 154.912 320.883 160.771Q331.467 166.63 336.003 174.379Q340.539 182.128 340.539 192.145Q340.539 204.43 334.302 213.407Q328.065 222.385 316.725 227.204Q305.385 232.024 290.076 232.024Q273.444 232.024 261.348 226.165Q249.252 220.306 242.354 209.816Q235.455 199.327 234.321 185.341L259.269 183.829Q260.403 192.523 264.278 198.571Q268.152 204.619 274.673 207.738Q281.193 210.856 290.454 210.856Q298.392 210.856 303.967 208.871Q309.543 206.887 312.473 203.012Q315.402 199.138 315.402 193.468Q315.402 188.365 312.945 184.302Q310.488 180.238 303.495 176.931Q296.502 173.623 282.705 170.41Q266.073 166.63 256.056 161.811Q246.039 156.991 241.503 149.715Q236.967 142.438 236.967 131.476Q236.967 121.49 241.222 113.494H230.373L221.129 113.559L220.872 116.68C218.081 149.875 216.796 162.8 214.666 177.819C210.443 208.113 205.817 221.002 196.747 227.905C193.075 230.659 189.843 231.76 185.657 231.54C181.398 231.357 178.497 229.961 175.633 226.767C172.989 223.829 172.034 221.405 172.034 217.476C172.034 213.18 172.915 211.234 177.616 204.992C182.646 198.309 184.629 195.077 188.411 187.55C196.783 170.769 200.896 153.877 204.421 122.004C204.825 118.369 205.266 114.954 205.376 114.367L205.596 113.375H201.043C191.569 113.412 185.878 114.44 180.37 117.047C174.568 119.801 169.721 124.354 166.196 130.34L164.25 133.645L162.193 133.388C161.092 133.241 160.137 133.094 160.063 133.02C159.88 132.874 161.422 127.108 162.45 124.061C164.029 119.361 166.416 114.293 168.656 110.915C171.153 107.17 176.11 102.029 179.268 99.899C185.951 95.419 193.626 92.849 202.402 92.114Z" />
    </svg>
  );
}

/** App SanityLoader: the cloud sweeping the hue wheel, centred. */
export function Loader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="sw-loader" role="status">
      <SanityLogo height={38} loader />
      <span className="sw-sr">{label}</span>
    </div>
  );
}

export function Greeting() {
  const now = new Date();
  const hour = now.getHours();
  const title = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="sw-greeting">
      <div className="sw-greeting-title">{title}</div>
    </div>
  );
}

export function StepCircles({ current, total = 3 }: { current: number; total?: number }) {
  return (
    <div className="sw-steps" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }, (_, k) => k + 1).map((i) => (
        <span key={i} className={i < current ? "sw-step sw-step-done" : i === current ? "sw-step sw-step-now" : "sw-step"}>
          {i < current ? <Check size={13} strokeWidth={3} /> : i}
        </span>
      ))}
    </div>
  );
}

/** AuthFrame: wallpaper, greeting, centred translucent card with logo, step circles, title, subtitle. */
export function Frame({ title, subtitle, step, greeting = true, children }: { title: string; subtitle?: string; step?: number; greeting?: boolean; children: ReactNode }) {
  return (
    <main className="sanity-web">
      {greeting ? <Greeting /> : null}
      <div className="sw-center">
        <section className="sw-card">
          <SanityLogo height={56} />
          {step ? <StepCircles current={step} /> : null}
          <h1 className="sw-title">{title}</h1>
          {subtitle ? <p className="sw-subtitle">{subtitle}</p> : null}
          <div className="sw-content">{children}</div>
        </section>
      </div>
    </main>
  );
}

export function PageLoader() {
  return (
    <main className="sanity-web sw-plain">
      <Loader />
    </main>
  );
}

export function OkDot() {
  return (
    <span className="sw-ok" aria-label="Done">
      <Check size={12} strokeWidth={3} />
    </span>
  );
}

export function Field({
  icon: Icon, placeholder, value, onChange, done = false, type = "text", autoComplete,
}: {
  icon: LucideIcon; placeholder: string; value: string; onChange: (v: string) => void; done?: boolean; type?: string; autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);
  const password = type === "password";
  return (
    <label className={password ? "sw-field sw-field-pw" : "sw-field"}>
      <Icon size={14} className="sw-field-icon" aria-hidden="true" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        type={password && visible ? "text" : type}
        autoComplete={autoComplete}
        autoCorrect="off"
        spellCheck={false}
      />
      {password ? (
        <button type="button" className="sw-eye" aria-label={visible ? "Hide password" : "Show password"} onClick={() => setVisible((v) => !v)}>
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      ) : null}
      {done ? <OkDot /> : null}
    </label>
  );
}

/** App Primary: 48px accent pill, white 16/600 label and arrow; greyed when disabled; shimmer while working. */
export function Primary({
  label, enabled = true, pending = false, arrow = true, onClick, href,
}: { label: string; enabled?: boolean; pending?: boolean; arrow?: boolean; onClick?: () => void; href?: string }) {
  const cls = pending ? "sw-btn sw-primary sw-pending" : enabled ? "sw-btn sw-primary" : "sw-btn sw-primary sw-disabled";
  const inner = (
    <>
      <span>{label}</span>
      {arrow && !pending ? <ArrowRight size={16} aria-hidden="true" /> : null}
      {pending ? <span className="sw-sr" role="status">Loading</span> : null}
    </>
  );
  if (href) return <a className={cls} href={href}>{inner}</a>;
  return (
    <button type="button" className={cls} disabled={!enabled || pending} aria-busy={pending || undefined} onClick={onClick}>
      {inner}
    </button>
  );
}

/** App Secondary: 48px outlined pill, 16px icon, 12.25/650 label. */
export function Secondary({ label, icon: Icon, onClick, href }: { label: string; icon?: LucideIcon; onClick?: () => void; href?: string }) {
  const inner = (
    <>
      {Icon ? <Icon size={16} aria-hidden="true" /> : null}
      <span>{label}</span>
    </>
  );
  if (href) return <a className="sw-btn sw-secondary" href={href}>{inner}</a>;
  return <button type="button" className="sw-btn sw-secondary" onClick={onClick}>{inner}</button>;
}

/** Onboarding BigButton: 50px pill; primary = accent, secondary = surface. */
export function BigButton({ label, primary = true, onClick, href }: { label: string; primary?: boolean; onClick?: () => void; href?: string }) {
  const cls = primary ? "sw-btn sw-big" : "sw-btn sw-big sw-big-alt";
  if (href) return <a className={cls} href={href}>{label}</a>;
  return <button type="button" className={cls} onClick={onClick}>{label}</button>;
}

export function AuthCopy({ children }: { children: ReactNode }) {
  return <p className="sw-copy">{children}</p>;
}

export function ErrorBox({ text }: { text: string }) {
  return <p className="sw-error" role="alert">{text}</p>;
}

export function ConfirmedLine({ text }: { text: string }) {
  return (
    <p className="sw-confirmed">
      <Check size={16} strokeWidth={3} aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}

/** PayStep "Payment not finished" panel. */
export function NotFinished({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="sw-panel">
      <p className="sw-panel-title">Payment not finished</p>
      <p className="sw-panel-line">Nothing was charged. Choose a payment option below, or try again if you already paid.</p>
      <BigButton label="Try again" onClick={onRetry} />
    </div>
  );
}

/** Onboarding step layout: wallpaper, 34px logo, step circles, StepTitle, content. */
export function StepFrame({ step, title, line, wide = false, children }: { step: number; title: string; line?: string; wide?: boolean; children: ReactNode }) {
  return (
    <main className="sanity-web sw-onboard">
      <SanityLogo height={34} />
      <StepCircles current={step} />
      <div className={wide ? "sw-step-body sw-step-wide" : "sw-step-body"}>
        <h1 className="sw-step-title">{title}</h1>
        {line ? <p className="sw-step-line">{line}</p> : null}
        {children}
      </div>
    </main>
  );
}

/** App AuthLink: small centred text link. */
export function AuthLink({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="sw-link" onClick={onClick}>{label}</button>;
}

/** Read-only value under the fields (the generated Yatishara email, the account being signed in). */
export function ReadOnlyLine({ label, value }: { label: string; value: string }) {
  return (
    <p className="sw-readonly">
      <span>{label}</span>
      <b>{value}</b>
    </p>
  );
}

/* Continue with Google: Google Identity Services, WEB client id. Our pill is what people see; the official
 * GIS button sits invisibly on top so Google gets the real click (GIS has no supported custom-button API). */
type GisId = {
  initialize: (o: { client_id: string; callback: (r: { credential?: string }) => void; ux_mode?: string; login_uri?: string; auto_select?: boolean; itp_support?: boolean }) => void;
  renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
};
let gisPromise: Promise<GisId> | null = null;
function loadGis(): Promise<GisId> {
  const w = window as unknown as { google?: { accounts?: { id?: GisId } } };
  if (w.google?.accounts?.id) return Promise.resolve(w.google.accounts.id);
  gisPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => (w.google?.accounts?.id ? resolve(w.google.accounts.id) : reject(new Error("Google sign-in did not load")));
    s.onerror = () => { gisPromise = null; reject(new Error("Google sign-in did not load")); };
    document.head.appendChild(s);
  });
  return gisPromise;
}

/** Official Google "G" mark (brand colours, unmodified). */
export function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** "or" rule between the email path and Continue with Google. */
export function OrRule() {
  return <div className="sw-or" role="separator"><span>or</span></div>;
}

/** Continue with Google: Secondary pill look with the G mark. clientId null = not configured (shown disabled). */
export function GoogleButton({ clientId, label = "Continue with Google", pending = false, onCredential, onError, preview = false, fill = false }: {
  clientId: string | null | undefined; label?: string; pending?: boolean; preview?: boolean;
  /** Render Google's button at its widest (400px) so it covers the whole box: taps on the icon or edges reach it too. */
  fill?: boolean;
  onCredential: (idToken: string) => void; onError: (message: string) => void;
}) {
  const overlay = useRef<HTMLDivElement | null>(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (preview) return;
    const m = document.cookie.match(new RegExp(`(?:^|; )${GSI_CREDENTIAL_COOKIE}=([^;]*)`));
    if (!m) return;
    document.cookie = `${GSI_CREDENTIAL_COOKIE}=; path=/; max-age=0; samesite=lax; secure`;
    const value = decodeURIComponent(m[1]);
    if (value === "error" || !value) onError("Google sign-in failed. Try again");
    else cb.current(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);
  useEffect(() => {
    if (preview || !clientId || !overlay.current) return;
    let live = true;
    loadGis().then((gis) => {
      if (!live || !overlay.current) return;
      const redirect = gsiShouldRedirect();
      if (redirect) document.cookie = `${GSI_RETURN_COOKIE}=${encodeURIComponent(location.pathname + location.search)}; path=/; max-age=600; samesite=lax; secure`;
      gis.initialize({
        client_id: clientId,
        callback: (r) => (r.credential ? cb.current(r.credential) : onError("Google sign-in failed. Try again")),
        ...(redirect ? { ux_mode: "redirect", login_uri: location.origin + GSI_CALLBACK_PATH } : { ux_mode: "popup" }),
        itp_support: true,
      });
      overlay.current.innerHTML = "";
      gis.renderButton(overlay.current, { type: "standard", theme: "outline", size: "large", shape: "pill", text: "continue_with", width: fill ? 400 : Math.min(400, Math.max(200, Math.round(overlay.current.offsetWidth || 320))) });
      setReady(true);
    }).catch((e) => onError(e instanceof Error ? e.message : "Google sign-in did not load"));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, preview]);
  const off = !preview && (!clientId || pending);
  return (
    <div className={off ? "sw-google sw-google-off" : "sw-google"}>
      <button type="button" className="sw-btn sw-secondary sw-google-btn" tabIndex={ready ? -1 : 0} disabled={off} aria-busy={pending || undefined}
        title={!preview && clientId === null ? "Google sign-in is not available yet" : undefined}>
        <GoogleG size={18} />
        <span>{label}</span>
      </button>
      {!preview && clientId ? <div ref={overlay} className="sw-google-gis" aria-label={label} /> : null}
    </div>
  );
}
